import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/Select";
import { Textarea } from "./ui/Textarea";
import { Switch } from "./ui/Switch";
import { X, Loader2, User, MapPin, Save, Settings } from "lucide-react";
import api from "../services/api";
import { customerService } from "../services/customers";
import { toast } from "sonner";
import { AsyncSelect } from "./AsyncSelect";
import { limitPercentage } from "../utils";

interface Client {
  id: number;
  name: string;
  email: string;
  phone: string;
  city: string;
  state?: string;
  type: string;
  status: string;
  supplierCode?: string;
  comission?: string;
  referral_commission_rate?: string | number;
  referred_commission_rate?: string | number;
  block_buy?: boolean;
  document?: string;
  referred_by_id?: number | null;
  referred_by?: any;
  address?: {
    zip?: string;
    city?: string;
    state?: string;
    number?: string;
    address?: string;
    country?: string;
    complement?: string;
    neighborhood?: string;
  };
  observacoes?: string;
}

interface EditarClienteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
  loggedInUser?: any;
  onSave: (client: Client) => void;
}

export function EditarClienteDialog({ open, onOpenChange, client, loggedInUser, onSave }: EditarClienteDialogProps) {
  const [loading, setLoading] = useState(false);
  const [showCepSearch, setShowCepSearch] = useState(false);
  const [searchingCep, setSearchingCep] = useState(false);
  const [cepSearchResults, setCepSearchResults] = useState<any[]>([]);

  const [cepSearchData, setCepSearchData] = useState({
    uf: "",
    cidade: "",
    logradouro: ""
  });

  const [formData, setFormData] = useState({
    id: 0,
    nome: "",
    sobrenome: "",
    email: "",
    celular: "",
    cpfCnpj: "",
    codigo: "",
    comissaoFdc: "",
    comissaoIndicacao: "",
    bloquearCompras: false,
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    observacoes: "",
    senha: "",
    referred_by_id: null as number | null
  });

  const [selectedNames, setSelectedNames] = useState<Record<string, string>>({});

  const fetchIndicators = useCallback(async (params: any) => {
    if (!params.search || params.search.trim().length < 2) return [];
    try {
      const res = await customerService.getAll({ search: params.search });
      const items = res.data?.data || res.data || [];
      return items.filter((c: any) => c.id !== formData.id);
    } catch (err) {
      console.error("Erro ao buscar indicadores:", err);
      return [];
    }
  }, [formData.id]);

  useEffect(() => {
    if (client) {
      const nameParts = client.name ? client.name.split(" ") : [""];
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
      const refId = client.referred_by_id || client.referred_by?.id || null;

      if (refId) {
        const refUser = client.referred_by;
        const codeVal = refUser?.code || refUser?.supplierCode || refId;
        const fullName = refUser
          ? ([refUser.first_name, refUser.last_name].filter(Boolean).join(" ") || refUser.name || "")
          : "Indicador";
        const emailStr = refUser?.email ? ` (${refUser.email})` : "";
        const initialReferredName = `[${codeVal}] ${fullName}${emailStr}`;
        setSelectedNames(prev => ({ ...prev, [refId.toString()]: initialReferredName }));
      }

      setFormData({
        id: client.id || 0,
        nome: firstName,
        sobrenome: lastName,
        email: client.email || "",
        celular: client.phone || "",
        cpfCnpj: client.document || "",
        codigo: client.supplierCode || "",
        comissaoFdc: client.comission || "",
        comissaoIndicacao: client.referred_commission_rate !== undefined ? String(client.referred_commission_rate) : "",
        bloquearCompras: client.block_buy || false,
        cep: client.address?.zip || "",
        endereco: client.address?.address || "",
        numero: client.address?.number || "",
        complemento: client.address?.complement || "",
        bairro: client.address?.neighborhood || "",
        cidade: client.address?.city || client.city || "",
        estado: client.address?.state || "",
        observacoes: "",
        senha: "",
        referred_by_id: refId ? Number(refId) : null
      });
    } else {
      setSelectedNames({});
      setFormData({
        id: 0,
        nome: "",
        sobrenome: "",
        email: "",
        celular: "",
        cpfCnpj: "",
        codigo: "",
        comissaoFdc: "",
        comissaoIndicacao: "",
        bloquearCompras: false,
        cep: "",
        endereco: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        estado: "",
        observacoes: "",
        senha: "",
        referred_by_id: null
      });
    }
  }, [client, open]);

  const handleCepChange = async (value: string) => {
    const cleanCep = value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, cep: cleanCep.substring(0, 8) }));

    if (cleanCep.length === 8) {
      try {
        const response = await api.get(`/api/cep/${cleanCep}`);
        const data = response.data;

        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            estado: data.uf
          }));
        } else {
          toast.error("CEP não encontrado");
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
        toast.error("Erro ao buscar CEP");
      }
    }
  };

  const handleSearchCepByAddress = async () => {
    if (!cepSearchData.uf || !cepSearchData.cidade || !cepSearchData.logradouro) {
      toast.error("Preencha UF, Cidade e Rua para buscar");
      return;
    }

    if (cepSearchData.cidade.length < 3 || cepSearchData.logradouro.length < 3) {
      toast.error("Cidade e Rua precisam de pelo menos 3 caracteres");
      return;
    }

    setSearchingCep(true);
    try {
      const response = await api.get(`/api/cep/${cepSearchData.uf}/${cepSearchData.cidade}/${cepSearchData.logradouro}`);
      const data = response.data;

      if (Array.isArray(data) && data.length > 0) {
        setCepSearchResults(data);
      } else {
        toast.error("Nenhum CEP encontrado para este endereço");
        setCepSearchResults([]);
      }
    } catch (error) {
      toast.error("Erro na busca de CEP");
    } finally {
      setSearchingCep(false);
    }
  };

  const selectAddressFromSearch = (addr: any) => {
    setFormData(prev => ({
      ...prev,
      cep: addr.cep.replace(/\D/g, ''),
      endereco: addr.logradouro,
      bairro: addr.bairro,
      cidade: addr.localidade,
      estado: addr.uf
    }));
    setShowCepSearch(false);
    setCepSearchResults([]);
    setCepSearchData({ uf: "", cidade: "", logradouro: "" });
  };

  const formatBRPhone = (v: string) => {
    const d = v.replace(/\D/g, "").substring(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 7) return `(${d.substring(0, 2)}) ${d.substring(2)}`;
    return `(${d.substring(0, 2)}) ${d.substring(2, 7)}-${d.substring(7)}`;
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, celular: formatBRPhone(value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome || formData.nome.trim() === "") {
      toast.error("O nome do cliente é obrigatório.");
      return;
    }

    if (!formData.email || formData.email.trim() === "") {
      toast.error("O e-mail do cliente é obrigatório.");
      return;
    }

    if (formData.referred_by_id !== null && (!formData.comissaoIndicacao || formData.comissaoIndicacao.trim() === "")) {
      toast.error("Ao selecionar um indicador, é necessário informar a porcentagem da Comissão de Indicação.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: formData.nome,
        last_name: formData.sobrenome,
        email: formData.email,
        phone: formData.celular,
        document: formData.cpfCnpj,
        code: formData.codigo,
        comission: formData.comissaoFdc === "" ? null : Number(formData.comissaoFdc),
        referred_commission_rate: formData.comissaoIndicacao === "" ? null : Number(formData.comissaoIndicacao),
        block_buy: formData.bloquearCompras,
        referred_by_id: formData.referred_by_id,
        address: {
          zip: formData.cep,
          address: formData.endereco,
          number: formData.numero,
          complement: formData.complemento,
          neighborhood: formData.bairro,
          city: formData.cidade,
          state: formData.estado,
          country: client?.address?.country || "Brasil"
        },
        observacoes: formData.observacoes
      };

      if (formData.senha) {
        (payload as any).password = formData.senha;
      }

      if (formData.id === 0) {
        await customerService.create(payload);
        toast.success("Cliente cadastrado com sucesso!");
      } else {
        await customerService.update(formData.id, payload);
        toast.success("Cliente atualizado com sucesso!");
      }

      onSave(payload as any);
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erro ao salvar cliente:", error);
      const serverErrors = error?.response?.data?.errors;
      if (serverErrors && typeof serverErrors === "object") {
        const errorMessages = Object.values(serverErrors).flat().filter(Boolean);
        if (errorMessages.length > 0) {
          errorMessages.forEach((msg: any) => toast.error(String(msg)));
          return;
        }
      }
      const message = error?.response?.data?.message || "Erro ao salvar cliente.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="bg-[#D4C070] p-2 rounded-lg">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{client ? 'Editar Cliente' : 'Novo Cliente'}</h2>
              <p className="text-sm text-gray-500">Atualize as informações cadastrais</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="rounded-full">
            <X className="h-6 w-6 text-gray-400" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-8 space-y-8">

            {/* Seção 1: Configurações */}
            <div>
              <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                <Settings className="h-5 w-5 text-[#D4C070]" />
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider">Ajustes da Conta</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="codigo" className="text-xs font-bold uppercase text-gray-500">Cód. Fornecedor</Label>
                  <Input
                    id="codigo"
                    value={formData.codigo}
                    onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
                    placeholder="Ex: F001"
                    className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="comissao-fdc" className="text-xs font-bold uppercase text-gray-500">Comissão FDC</Label>
                  <div className="relative">
                    <Input
                      id="comissao-fdc"
                      type="text"
                      value={formData.comissaoFdc}
                      onChange={(e) => {
                        const limited = limitPercentage(e.target.value);
                        setFormData(prev => ({ ...prev, comissaoFdc: limited }));
                      }}
                      placeholder="0"
                      className="bg-gray-50 border-gray-200 focus:ring-[#D4C070] pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                  </div>
                </div>
                <div className="flex items-center pt-6 md:col-span-2">
                  <Switch
                    id="bloquear-compras"
                    checked={formData.bloquearCompras}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bloquearCompras: checked }))}
                  />
                  <Label htmlFor="bloquear-compras" className="ml-2 text-sm font-bold text-gray-700 cursor-pointer">Bloquear Compras</Label>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="referred_by_id" className="text-xs font-bold uppercase text-gray-500">Indicado Por</Label>
                  <AsyncSelect
                    value={formData.referred_by_id ? String(formData.referred_by_id) : null}
                    onChange={(id, lbl) => {
                      setFormData(prev => ({ ...prev, referred_by_id: id ? Number(id) : null }));
                      if (id) {
                        setSelectedNames(prev => ({ ...prev, [id]: lbl }));
                      }
                    }}
                    fetcher={fetchIndicators}
                    placeholder="Buscar por código, nome ou e-mail..."
                    initialDisplay={formData.referred_by_id ? selectedNames[String(formData.referred_by_id)] : ""}
                    getOptionLabel={(u) => {
                      const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                      const codeVal = u.code || u.supplierCode || u.id;
                      return `[${codeVal}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                    }}
                    renderOption={(u) => (
                      <div className="flex items-center gap-3 py-1.5 w-full">
                        <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                          {u.code || u.supplierCode || u.id}
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="font-bold text-gray-900 truncate">
                            {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name}
                          </span>
                          {u.email && <span className="text-[10px] text-gray-500 truncate font-medium">{u.email}</span>}
                        </div>
                      </div>
                    )}
                  />
                </div>

                {formData.referred_by_id !== null && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="comissao-indicacao" className="text-xs font-bold uppercase text-[#D4C070] flex items-center gap-1">
                      Comissão Indicação <span className="text-red-500">*</span> <span className="text-[10px] text-gray-400 font-normal lowercase">(obrigatório ao definir um indicador)</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="comissao-indicacao"
                        type="text"
                        value={formData.comissaoIndicacao}
                        onChange={(e) => {
                          const limited = limitPercentage(e.target.value);
                          setFormData(prev => ({ ...prev, comissaoIndicacao: limited }));
                        }}
                        placeholder="Informe a porcentagem (ex: 5)"
                        className="bg-gray-50 border-[#D4C070]/50 focus:ring-[#D4C070] pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Seção 2: Identificação */}
            <div>
              <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                <User className="h-5 w-5 text-[#D4C070]" />
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider">Identificação do Cliente</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="cpfCnpj" className="text-xs font-bold uppercase text-gray-500">CPF / CNPJ</Label>
                  <Input
                    id="cpfCnpj"
                    placeholder="Somente números"
                    value={formData.cpfCnpj}
                    onChange={e => setFormData({ ...formData, cpfCnpj: e.target.value.replace(/\D/g, '') })}
                    className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="nome" className="text-xs font-bold uppercase text-gray-500">Nome Completo</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      id="nome"
                      placeholder="Nome"
                      value={formData.nome}
                      onChange={e => setFormData({ ...formData, nome: e.target.value })}
                      className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                    />
                    <Input
                      id="sobrenome"
                      placeholder="Sobrenome"
                      value={formData.sobrenome}
                      onChange={e => setFormData({ ...formData, sobrenome: e.target.value })}
                      className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold uppercase text-gray-500">E-mail <span className="text-red-500">*</span></Label>
                  <Input id="email" type="email" required placeholder="email@exemplo.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="celular" className="text-xs font-bold uppercase text-gray-500">Celular</Label>
                  <Input
                    id="celular"
                    placeholder="(00) 00000-0000"
                    value={formData.celular}
                    onChange={e => handlePhoneChange(e.target.value)}
                    className="bg-gray-50 border-gray-200"
                  />
                </div>

                {loggedInUser?.email === 'ronald@ronald.com' && (
                  <div className="space-y-2">
                    <Label htmlFor="senha" className="text-xs font-bold uppercase text-gray-500">
                      Nova Senha {client ? '(Opcional)' : '(Opcional)'}
                    </Label>
                    <Input
                      id="senha"
                      type="text"
                      placeholder="Deixe em branco para manter"
                      value={formData.senha}
                      onChange={e => setFormData({ ...formData, senha: e.target.value })}
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Seção 3: Endereço */}
            <div>
              <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                <MapPin className="h-5 w-5 text-[#D4C070]" />
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider">Endereço</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="cep" className="text-xs font-bold uppercase text-gray-500">CEP</Label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCepSearch(!showCepSearch)}
                      className="text-[10px] text-[#D4C070] font-bold hover:underline uppercase"
                    >
                      {showCepSearch ? "Voltar ao CEP" : "Não sei o CEP"}
                    </button>
                  </div>
                  <Input
                    id="cep"
                    placeholder="00000000"
                    value={formData.cep}
                    onChange={e => handleCepChange(e.target.value)}
                    className="bg-white border-gray-200 focus:ring-[#D4C070]"
                  />
                </div>
              </div>

              {/* Busca de CEP por Rua */}
              {showCepSearch && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-[#D4C070]/20 space-y-4">
                  <p className="text-xs font-bold text-[#D4C070] uppercase">Busca por Logradouro</p>
                  <div className="grid grid-cols-4 gap-3">
                    <Input placeholder="UF" maxLength={2} value={cepSearchData.uf} onChange={e => setCepSearchData({ ...cepSearchData, uf: e.target.value.toUpperCase() })} className="bg-white uppercase" />
                    <Input placeholder="Cidade" value={cepSearchData.cidade} onChange={e => setCepSearchData({ ...cepSearchData, cidade: e.target.value })} className="bg-white col-span-1" />
                    <Input placeholder="Rua / Logradouro" value={cepSearchData.logradouro} onChange={e => setCepSearchData({ ...cepSearchData, logradouro: e.target.value })} className="bg-white col-span-2" />
                  </div>
                  <Button type="button" onClick={handleSearchCepByAddress} disabled={searchingCep} className="w-full bg-white border border-[#D4C070] text-[#D4C070] hover:bg-[#D4C070]/10 text-xs font-bold">
                    {searchingCep ? <Loader2 className="h-4 w-4 animate-spin" /> : "BUSCAR CEP"}
                  </Button>

                  {cepSearchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-2 mt-2 custom-scrollbar pr-2">
                      {cepSearchResults.map((addr, i) => (
                        <div
                          key={i}
                          onClick={() => selectAddressFromSearch(addr)}
                          className="p-2 bg-white border border-gray-100 rounded hover:border-[#D4C070] cursor-pointer text-[10px]"
                        >
                          <p className="font-bold">{addr.logradouro}</p>
                          <p className="text-gray-500">{addr.bairro} - {addr.localidade}/{addr.uf} - {addr.cep}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="endereco" className="text-xs font-bold uppercase text-gray-500">Endereço</Label>
                  <Input id="endereco" value={formData.endereco} onChange={e => setFormData({ ...formData, endereco: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero" className="text-xs font-bold uppercase text-gray-500">Número</Label>
                  <Input id="numero" value={formData.numero} onChange={e => setFormData({ ...formData, numero: e.target.value })} className="bg-white border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complemento" className="text-xs font-bold uppercase text-gray-500">Complemento</Label>
                  <Input id="complemento" value={formData.complemento} onChange={e => setFormData({ ...formData, complemento: e.target.value })} className="bg-white border-gray-200" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="bairro" className="text-xs font-bold uppercase text-gray-500">Bairro</Label>
                  <Input id="bairro" value={formData.bairro} onChange={e => setFormData({ ...formData, bairro: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cidade" className="text-xs font-bold uppercase text-gray-500">Cidade</Label>
                  <Input id="cidade" value={formData.cidade} onChange={e => setFormData({ ...formData, cidade: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado" className="text-xs font-bold uppercase text-gray-500">Estado (UF)</Label>
                  <Select value={formData.estado} onValueChange={(val) => setFormData(prev => ({ ...prev, estado: val }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]">
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SP">SP</SelectItem>
                      <SelectItem value="RJ">RJ</SelectItem>
                      <SelectItem value="MG">MG</SelectItem>
                      <SelectItem value="RS">RS</SelectItem>
                      <SelectItem value="PR">PR</SelectItem>
                      <SelectItem value="BA">BA</SelectItem>
                      <SelectItem value="SC">SC</SelectItem>
                      <SelectItem value="DF">DF</SelectItem>
                      <SelectItem value="GO">GO</SelectItem>
                      <SelectItem value="PE">PE</SelectItem>
                      <SelectItem value="CE">CE</SelectItem>
                      <SelectItem value="ES">ES</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Observações */}
              <div className="mt-6 space-y-2">
                <Label htmlFor="observacoes" className="text-xs font-bold uppercase text-gray-500">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Informações adicionais sobre o cliente..."
                  rows={3}
                  className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                />
              </div>

            </div>

            {/* Resumo e Botões */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/30 flex flex-col sm:flex-row gap-4 mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1 bg-white border-gray-300 text-gray-600 hover:bg-gray-50 h-12 font-bold text-sm rounded-xl"
              >
                CANCELAR
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#D4C070] hover:bg-[#849D70] text-white h-12 font-bold text-sm shadow-lg shadow-[#D4C070]/20 rounded-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    SALVANDO...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-5 w-5" />
                    SALVAR ALTERAÇÕES
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
